import { addSeconds, differenceInSeconds, isValid, parseISO } from "date-fns"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { convertToTimezone } from "@/lib/utils"

interface UseCountdownParams {
	targetTime?: Date | string | null // 绝对目标时间
	startTime?: Date | string | null // 起始时间
	durationInSeconds?: number // 持续秒数
	onEnd?: () => void
	onExpiredRefetch?: () => void // 倒计时结束后持续刷新数据的回调
}

interface CountdownValues {
	days: string
	hours: string
	minutes: string
	seconds: string
}

interface FormattedTime {
	days: string
	hours: string
	minutes: string
	seconds: string
}

interface UseTimezoneCountdownProps {
	targetTime?: Date | string | null
	timezone?: string
}

interface UseTimezoneCountdownReturn {
	formattedTime: FormattedTime
	isRunning: boolean
}

export const useCountdown = ({
	targetTime,
	startTime,
	durationInSeconds,
	onEnd,
	onExpiredRefetch,
}: UseCountdownParams) => {
	const [endTime, setEndTime] = useState<Date | null>(null)
	const [hasTriggeredEnd, setHasTriggeredEnd] = useState(false)

	// 使用ref来保存最新的回调，避免依赖变化导致effect重新执行
	const onEndRef = useRef(onEnd)
	const onExpiredRefetchRef = useRef(onExpiredRefetch)

	// 更新ref中的回调
	useEffect(() => {
		onEndRef.current = onEnd
		onExpiredRefetchRef.current = onExpiredRefetch
	}, [onEnd, onExpiredRefetch])

	// 决定倒计时的最终结束时间
	useEffect(() => {
		let finalEndTime: Date | null = null

		// 优先使用绝对目标时间
		if (targetTime) {
			const date =
				typeof targetTime === "string" ? parseISO(targetTime) : targetTime
			if (isValid(date)) {
				finalEndTime = date
			}
		}
		// 其次，如果提供了起始时间和持续时长，则计算结束时间
		else if (startTime && durationInSeconds) {
			const startDate =
				typeof startTime === "string" ? parseISO(startTime) : startTime
			if (isValid(startDate)) {
				finalEndTime = addSeconds(startDate, durationInSeconds)
			}
		}

		setEndTime(finalEndTime)
		// 重置触发状态，当结束时间改变时
		setHasTriggeredEnd(false)
	}, [targetTime, startTime, durationInSeconds])

	// 计算剩余时间的函数
	const calculateTimeLeft = useCallback((end: Date): number => {
		const now = new Date()
		const secondsLeft = differenceInSeconds(end, now)
		return Math.max(0, secondsLeft)
	}, [])

	const [timeLeft, setTimeLeft] = useState<number | null>(() => {
		if (!endTime) return null
		return calculateTimeLeft(endTime)
	})

	// 更新剩余时间的函数
	const updateTimeLeft = useCallback(() => {
		if (!endTime) {
			setTimeLeft(null)
			return
		}

		const newTimeLeft = calculateTimeLeft(endTime)
		setTimeLeft(newTimeLeft)

		// 如果时间已到且尚未触发结束回调，则触发一次
		if (newTimeLeft <= 0 && !hasTriggeredEnd) {
			setHasTriggeredEnd(true)
			onEndRef.current?.()
		}
	}, [endTime, calculateTimeLeft, hasTriggeredEnd])

	// 主倒计时逻辑
	useEffect(() => {
		if (!endTime) {
			setTimeLeft(null)
			return
		}

		// 立即计算一次初始剩余时间
		updateTimeLeft()

		const interval = setInterval(() => {
			updateTimeLeft()
		}, 1000)

		return () => clearInterval(interval)
	}, [endTime, updateTimeLeft])

	// 倒计时结束后的持续刷新逻辑
	useEffect(() => {
		if (!hasTriggeredEnd || !onExpiredRefetchRef.current) {
			return
		}

		// 倒计时结束后，每2秒刷新一次数据，直到组件卸载或状态改变
		const refetchInterval = setInterval(() => {
			onExpiredRefetchRef.current?.()
		}, 2000)

		return () => clearInterval(refetchInterval)
	}, [hasTriggeredEnd])

	// 页面可见性变化处理
	useEffect(() => {
		const handleVisibilityChange = () => {
			if (!document.hidden && endTime) {
				// 页面重新变为可见时，立即更新倒计时
				updateTimeLeft()
			}
		}

		// 页面焦点事件处理
		const handleFocus = () => {
			if (endTime) {
				updateTimeLeft()
			}
		}

		document.addEventListener("visibilitychange", handleVisibilityChange)
		window.addEventListener("focus", handleFocus)

		return () => {
			document.removeEventListener("visibilitychange", handleVisibilityChange)
			window.removeEventListener("focus", handleFocus)
		}
	}, [endTime, updateTimeLeft])

	const formattedTime: CountdownValues = useMemo(() => {
		if (timeLeft === null || timeLeft < 0) {
			return { days: "00", hours: "00", minutes: "00", seconds: "00" }
		}

		const days = Math.floor(timeLeft / (3600 * 24))
		const hours = Math.floor((timeLeft % (3600 * 24)) / 3600)
		const minutes = Math.floor((timeLeft % 3600) / 60)
		const seconds = timeLeft % 60

		return {
			days: String(days).padStart(2, "0"),
			hours: String(hours).padStart(2, "0"),
			minutes: String(minutes).padStart(2, "0"),
			seconds: String(seconds).padStart(2, "0"),
		}
	}, [timeLeft])

	const isRunning = timeLeft !== null && timeLeft > 0

	return { timeLeft, formattedTime, isRunning }
}

export function useTimezoneCountdown({
	targetTime,
	timezone,
}: UseTimezoneCountdownProps): UseTimezoneCountdownReturn {
	const [formattedTime, setFormattedTime] = useState<FormattedTime>({
		days: "00",
		hours: "00",
		minutes: "00",
		seconds: "00",
	})
	const [isRunning, setIsRunning] = useState(false)

	useEffect(() => {
		if (!targetTime) {
			setIsRunning(false)
			return
		}

		function calculateTimeLeft() {
			const now = new Date()
			let target: Date

			if (timezone && targetTime) {
				// 如果提供了时区，使用convertToTimezone转换目标时间
				target = convertToTimezone(targetTime.toString(), timezone)
			} else if (targetTime) {
				// 如果没有提供时区，直接使用目标时间
				target =
					typeof targetTime === "string" ? new Date(targetTime) : targetTime
			} else {
				// 如果没有targetTime，返回默认值
				setIsRunning(false)
				return {
					days: "00",
					hours: "00",
					minutes: "00",
					seconds: "00",
				}
			}

			const difference = target.getTime() - now.getTime()

			if (difference <= 0) {
				setIsRunning(false)
				return {
					days: "00",
					hours: "00",
					minutes: "00",
					seconds: "00",
				}
			}

			setIsRunning(true)

			const days = Math.floor(difference / (1000 * 60 * 60 * 24))
			const hours = Math.floor((difference / (1000 * 60 * 60)) % 24)
			const minutes = Math.floor((difference / 1000 / 60) % 60)
			const seconds = Math.floor((difference / 1000) % 60)

			return {
				days: String(days).padStart(2, "0"),
				hours: String(hours).padStart(2, "0"),
				minutes: String(minutes).padStart(2, "0"),
				seconds: String(seconds).padStart(2, "0"),
			}
		}

		const timer = setInterval(() => {
			setFormattedTime(calculateTimeLeft())
		}, 1000)

		setFormattedTime(calculateTimeLeft())

		return () => clearInterval(timer)
	}, [targetTime, timezone])

	return { formattedTime, isRunning }
}
